import { useEffect, useState, lazy, Suspense } from 'react'

import { ShortcutHelpPanel, HelpPanel, HelpButton } from '@elsapiens/help'
import { ThemeProvider, LocaleProvider, ShortcutProvider, HelpProvider } from '@elsapiens/providers'
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'


import { AppearanceSync } from './components/AppearanceSync'
import { AuthGuard } from './components/AuthGuard'
import { ErrorBoundary } from './components/ErrorBoundary'
import { AppLayout } from './components/layout'
import { ServicesDownPage } from './components/ServicesDownPage'
import { SetupGuard } from './components/SetupGuard'
import { ToastProvider } from './components/ui'
import { checkServicesHealth } from './services/health-check'
import { LedgerProvider } from './stores/ledger-store'
import { OrganizationProvider } from './stores/organization-store'
import { SettingsProvider } from './stores/settings-store'
import { COOKIE_DOMAIN } from './utils/domain'

// Lazy load pages for code splitting
// Note: Pages use named exports, so we transform them to default exports for lazy loading
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })))
const ChartOfAccounts = lazy(() => import('./pages/ChartOfAccounts').then(m => ({ default: m.ChartOfAccounts })))
const Organizations = lazy(() => import('./pages/Organizations').then(m => ({ default: m.Organizations })))
const Customers = lazy(() => import('./pages/customers/CustomersPage').then(m => ({ default: m.Customers })))
const Suppliers = lazy(() => import('./pages/suppliers/SuppliersPage').then(m => ({ default: m.Suppliers })))
const Entities = lazy(() => import('./pages/Entities').then(m => ({ default: m.Entities })))
const EntityDetail = lazy(() => import('./pages/EntityDetail').then(m => ({ default: m.EntityDetail })))
const JournalEntries = lazy(() => import('./pages/JournalEntries').then(m => ({ default: m.JournalEntries })))
const JournalEntryForm = lazy(() => import('./pages/JournalEntryForm').then(m => ({ default: m.JournalEntryForm })))
const QuickEntry = lazy(() => import('./pages/QuickEntry').then(m => ({ default: m.QuickEntry })))
const Invoices = lazy(() => import('./pages/Invoices').then(m => ({ default: m.Invoices })))
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders').then(m => ({ default: m.PurchaseOrders })))
const SalesOrders = lazy(() => import('./pages/CustomerPurchaseOrders').then(m => ({ default: m.CustomerPurchaseOrders })))
const FiscalPeriods = lazy(() => import('./pages/FiscalPeriods').then(m => ({ default: m.FiscalPeriods })))
const OpeningBalances = lazy(() => import('./pages/opening-balances/OpeningBalancesPage').then(m => ({ default: m.OpeningBalancesPage })))
const DocumentNumbering = lazy(() => import('./pages/DocumentNumbering').then(m => ({ default: m.DocumentNumbering })))
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })))
const CompanySetup = lazy(() => import('./pages/CompanySetup').then(m => ({ default: m.CompanySetup })))
const CurrencyConversion = lazy(() => import('./pages/CurrencyConversion').then(m => ({ default: m.CurrencyConversion })))
const CurrencySetup = lazy(() => import('./pages/CurrencySetup').then(m => ({ default: m.CurrencySetup })))
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })))
const Payments = lazy(() => import('./pages/Payments').then(m => ({ default: m.Payments })))
const BankReconciliation = lazy(() => import('./pages/BankReconciliation').then(m => ({ default: m.BankReconciliation })))
const FormBuilder = lazy(() => import('./pages/FormBuilder').then(m => ({ default: m.FormBuilder })))
const RoleDefinitions = lazy(() => import('./pages/role-definitions').then(m => ({ default: m.RoleDefinitionsPage })))
const EntityImport = lazy(() => import('./pages/import/EntityImport').then(m => ({ default: m.EntityImport })))

// Lazy load report pages
const TrialBalance = lazy(() => import('./pages/reports/TrialBalance').then(m => ({ default: m.TrialBalance })))
const IncomeStatement = lazy(() => import('./pages/reports/ProfitAndLoss').then(m => ({ default: m.ProfitAndLoss })))
const BalanceSheet = lazy(() => import('./pages/reports/BalanceSheet').then(m => ({ default: m.BalanceSheet })))
const GeneralLedger = lazy(() => import('./pages/reports/GeneralLedger').then(m => ({ default: m.GeneralLedger })))
const ReceivableAging = lazy(() => import('./pages/reports/ReceivableAging').then(m => ({ default: m.ReceivableAging })))
const PayableAging = lazy(() => import('./pages/reports/PayableAging').then(m => ({ default: m.PayableAging })))
const CashFlow = lazy(() => import('./pages/reports/CashFlow').then(m => ({ default: m.CashFlow })))
const GSTReconciliation = lazy(() => import('./pages/reports/GSTReconciliation').then(m => ({ default: m.GSTReconciliation })))
const UnbalancedEntries = lazy(() => import('./pages/reports/UnbalancedEntries').then(m => ({ default: m.UnbalancedEntries })))

// Lazy load period-end / asset pages
const FixedAssets = lazy(() => import('./pages/FixedAssets').then(m => ({ default: m.FixedAssets })))
const PrepaidSchedules = lazy(() => import('./pages/PrepaidSchedules').then(m => ({ default: m.PrepaidSchedules })))
const DeferredRevenue = lazy(() => import('./pages/DeferredRevenue').then(m => ({ default: m.DeferredRevenue })))
const InventoryPage = lazy(() => import('./pages/Inventory').then(m => ({ default: m.Inventory })))
const InventoryItemPage = lazy(() => import('./pages/inventory/InventoryItemPage').then(m => ({ default: m.InventoryItemPage })))

// Lazy load hub pages
const AccountingHub = lazy(() => import('./pages/hubs/AccountingHub').then(m => ({ default: m.AccountingHub })))
const SalesHub = lazy(() => import('./pages/hubs/SalesHub').then(m => ({ default: m.SalesHub })))
const PurchasesHub = lazy(() => import('./pages/hubs/PurchasesHub').then(m => ({ default: m.PurchasesHub })))
const BankingHub = lazy(() => import('./pages/hubs/BankingHub').then(m => ({ default: m.BankingHub })))
const ReportsHub = lazy(() => import('./pages/hubs/ReportsHub').then(m => ({ default: m.ReportsHub })))
const PeriodEndHub = lazy(() => import('./pages/hubs/PeriodEndHub').then(m => ({ default: m.PeriodEndHub })))

// Admin layout
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout').then(m => ({ default: m.AdminLayout })))
const AdministrationHub = lazy(() => import('./pages/hubs/AdministrationHub').then(m => ({ default: m.AdministrationHub })))

// Settings sub-pages
const CompanySettingsPage   = lazy(() => import('./pages/settings/sections/CompanySettingsPage').then(m => ({ default: m.CompanySettingsPage })))
const DocumentSettingsPage  = lazy(() => import('./pages/settings/sections/DocumentSettingsPage').then(m => ({ default: m.DocumentSettingsPage })))
const PreferencesPage       = lazy(() => import('./pages/settings/sections/PreferencesPage').then(m => ({ default: m.PreferencesPage })))
const EntityScoringPage     = lazy(() => import('./pages/settings/sections/EntityScoringPage').then(m => ({ default: m.EntityScoringPage })))
const AgingBucketsPage      = lazy(() => import('./pages/settings/sections/AgingBucketsPage').then(m => ({ default: m.AgingBucketsPage })))
const TermsDeclarationPage  = lazy(() => import('./pages/settings/sections/TermsDeclarationPage').then(m => ({ default: m.TermsDeclarationPage })))

// Loading component for Suspense fallback
const PageLoader = (): React.ReactElement => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
      <p className="text-foreground-secondary">Loading...</p>
    </div>
  </div>
)

// Custom hook to check service health
function useServiceHealth(): boolean | null {
  const [isHealthy, setIsHealthy] = useState<boolean | null>(null)
  useEffect(() => {
    const check = async (): Promise<void> => {
      // Start time for minimum display duration
      const startTime = Date.now()
      const MIN_DISPLAY_TIME = 600 // Minimum 600ms to prevent flickering

      const health = await checkServicesHealth()

      // Calculate remaining time to show loader
      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsed)

      // Wait for minimum display time if needed
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }

      setIsHealthy(health.isHealthy)
    }
    void check()
  }, [])
  return isHealthy
}

// Error icon for route error boundary
const ErrorIcon = (): React.ReactElement => (
  <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
  </svg>
)

// Error component for route errors
const RouteErrorBoundary = (): React.ReactElement => (
  <div className="h-screen overflow-y-auto bg-background flex items-center justify-center p-4">
    <div className="max-w-2xl w-full bg-surface rounded-lg shadow-lg p-8">
      <div className="flex justify-center mb-6">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
          <ErrorIcon />
        </div>
      </div>
      <h1 className="text-2xl font-bold text-foreground text-center mb-2">Page Error</h1>
      <p className="text-foreground-secondary text-center mb-6">
        We encountered an error loading this page. Please try refreshing or navigate to a different page.
      </p>
      <div className="flex gap-3 justify-center">
        <button className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted transition-colors font-medium" onClick={() => window.location.reload()}>
          Reload Page
        </button>
        <button className="px-4 py-2 bg-primary-solid text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium" onClick={() => { window.location.href = '/' }}>
          Go to Home
        </button>
      </div>
    </div>
  </div>
)

// Create router configuration
const router = createBrowserRouter([
  // Company Setup & Selection - Full Page (No Sidebar)
  {
    path: '/setup/:step?',
    element: (
      <Suspense fallback={<PageLoader />}>
        <CompanySetup />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/organizations',
    element: (
      <Suspense fallback={<PageLoader />}>
        <Organizations />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  // Main App - With Layout and Setup Guard
  {
    element: (
      <SetupGuard>
        <AppLayout />
      </SetupGuard>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      // ── Overview ──
      { path: '/', element: <Dashboard /> },

      // ── Hub pages ──
      { path: '/accounting', element: <AccountingHub /> },
      { path: '/sales',      element: <SalesHub /> },
      { path: '/purchases',  element: <PurchasesHub /> },
      { path: '/banking',    element: <BankingHub /> },
      { path: '/period-end', element: <PeriodEndHub /> },

      // ── Accounting ──
      { path: '/accounts',            element: <ChartOfAccounts /> },
      { path: '/chart-of-accounts',   element: <ChartOfAccounts /> },
      { path: '/opening-balances',    element: <OpeningBalances /> },
      { path: '/journal',             element: <JournalEntries /> },
      { path: '/journal/:id',         element: <JournalEntryForm /> },
      { path: '/quick-entry',         element: <QuickEntry /> },
      { path: '/currency-conversion', element: <CurrencyConversion /> },

      // ── Import ──
      { path: '/import/entities',        element: <EntityImport /> },

      // ── Sales ──
      { path: '/customers',              element: <Customers /> },
      { path: '/sales-orders',           element: <SalesOrders /> },
      { path: '/sales-orders/new',       element: <SalesOrders /> },
      { path: '/sales-orders/:id',       element: <SalesOrders /> },
      { path: '/sales-orders/:id/edit',  element: <SalesOrders /> },
      { path: '/invoices',               element: <Navigate replace to="/invoices/proforma" /> },
      { path: '/invoices/new',           element: <Navigate replace to="/invoices/proforma/new" /> },
      { path: '/invoices/proforma',      element: <Invoices /> },
      { path: '/invoices/proforma/new',  element: <Invoices /> },
      { path: '/invoices/proforma/:id',  element: <Invoices /> },
      { path: '/invoices/proforma/:id/edit', element: <Invoices /> },
      { path: '/invoices/final',         element: <Invoices /> },
      { path: '/invoices/final/:id',     element: <Invoices /> },
      { path: '/invoices/final/:id/edit',element: <Invoices /> },
      { path: '/invoices/:id',           element: <Invoices /> },
      { path: '/invoices/:id/edit',      element: <Invoices /> },

      // ── Purchases ──
      { path: '/suppliers',              element: <Suppliers /> },
      { path: '/purchase-orders',        element: <PurchaseOrders /> },
      { path: '/bills',                  element: <Navigate replace to="/bills/draft" /> },
      { path: '/bills/draft',            element: <Invoices /> },
      { path: '/bills/draft/new',        element: <Invoices /> },
      { path: '/bills/draft/:id',        element: <Invoices /> },
      { path: '/bills/draft/:id/edit',   element: <Invoices /> },
      { path: '/bills/final',            element: <Invoices /> },
      { path: '/bills/final/:id',        element: <Invoices /> },
      { path: '/bills/final/:id/edit',   element: <Invoices /> },

      // ── Banking ──
      { path: '/payments',              element: <Payments /> },
      { path: '/payments/:id',          element: <Payments /> },
      { path: '/bank-reconciliation',   element: <BankReconciliation /> },

      // ── Reports ──
      { path: '/reports',                    element: <ReportsHub /> },
      { path: '/reports/trial-balance',      element: <TrialBalance /> },
      { path: '/reports/income-statement',   element: <IncomeStatement /> },
      { path: '/reports/balance-sheet',      element: <BalanceSheet /> },
      { path: '/reports/general-ledger',     element: <GeneralLedger /> },
      { path: '/reports/receivable-aging',   element: <ReceivableAging /> },
      { path: '/reports/payable-aging',      element: <PayableAging /> },
      { path: '/reports/cash-flow',          element: <CashFlow /> },
      { path: '/reports/gst-reconciliation', element: <GSTReconciliation /> },
      { path: '/reports/unbalanced-entries', element: <UnbalancedEntries /> },

      // ── Period End ──
      { path: '/fixed-assets',      element: <FixedAssets /> },
      { path: '/prepaid-schedules', element: <PrepaidSchedules /> },
      { path: '/deferred-revenue',  element: <DeferredRevenue /> },
      { path: '/inventory',         element: <InventoryPage /> },
      { path: '/inventory/:itemId', element: <InventoryItemPage /> },

      // ── Legacy redirects ──
      { path: '/entities',                element: <Entities /> },
      { path: '/entities/:partyId',       element: <EntityDetail /> },
      { path: '/entities/import',         element: <EntityImport /> },
      { path: '/periods',           element: <Navigate replace to="/admin/periods" /> },
      { path: '/numbering',         element: <Navigate replace to="/admin/numbering" /> },
      { path: '/currencies',        element: <Navigate replace to="/admin/currencies" /> },
      { path: '/form-builder',      element: <Navigate replace to="/admin/form-builder" /> },
      { path: '/role-definitions',  element: <Navigate replace to="/admin/roles" /> },
      { path: '/settings',          element: <Navigate replace to="/admin/settings/company" /> },

      // ── Administration (nested layout) ──
      {
        path: '/admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <AdministrationHub /> },
          { path: 'entities',          element: <Navigate replace to="/entities" /> },
          { path: 'entities/:partyId', element: <Navigate replace to="/entities" /> },
          { path: 'entities/import',   element: <Navigate replace to="/entities/import" /> },
          { path: 'periods',           element: <FiscalPeriods /> },
          { path: 'currencies',        element: <CurrencySetup /> },
          { path: 'numbering',         element: <DocumentNumbering /> },
          { path: 'form-builder',      element: <FormBuilder /> },
          { path: 'form-builder/new',  element: <FormBuilder /> },
          { path: 'form-builder/:id',  element: <FormBuilder /> },
          { path: 'roles',             element: <RoleDefinitions /> },
          {
            path: 'settings',
            element: <Settings />,
            children: [
              { index: true, element: <Navigate replace to="/admin/settings/company" /> },
              { path: 'company',          element: <CompanySettingsPage /> },
              { path: 'documents',        element: <DocumentSettingsPage /> },
              { path: 'terms-declaration', element: <TermsDeclarationPage /> },
              { path: 'preferences',      element: <PreferencesPage /> },
              { path: 'entity-scoring',   element: <EntityScoringPage /> },
              { path: 'aging-buckets',    element: <AgingBucketsPage /> },
            ],
          },
        ],
      },
    ],
  },
  // 404 Not Found
  {
    path: '*',
    element: (
      <Suspense fallback={<PageLoader />}>
        <NotFound />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
])

// Provider tree wrapping the router and global help/shortcut UI.
const AppProviders = (): React.ReactElement => (
  <ErrorBoundary>
    <ThemeProvider defaultTheme="system" rootDomain={COOKIE_DOMAIN}>
      <AuthGuard>
        <AppearanceSync>
          <LocaleProvider rootDomain={COOKIE_DOMAIN}>
            <OrganizationProvider>
              <SettingsProvider>
                <LedgerProvider>
                  <HelpProvider config={{ helpPanelPosition: 'right' }}>
                    <ShortcutProvider helpPanelShortcut="?">
                      <ToastProvider>
                        <RouterProvider router={router} />
                      </ToastProvider>
                      <ShortcutHelpPanel />
                      <HelpPanel />
                      <HelpButton position="bottom-right" size="md" />
                    </ShortcutProvider>
                  </HelpProvider>
                </LedgerProvider>
              </SettingsProvider>
            </OrganizationProvider>
          </LocaleProvider>
        </AppearanceSync>
      </AuthGuard>
    </ThemeProvider>
  </ErrorBoundary>
)

function App(): React.ReactElement {
  const isServicesHealthy = useServiceHealth()
  const [shouldShowApp, setShouldShowApp] = useState(false)

  useEffect(() => {
    if (isServicesHealthy !== null) {
      // Small delay before showing app for smooth fade-in
      setTimeout(() => {
        setShouldShowApp(true)
      }, 50)
    }
  }, [isServicesHealthy])

  if (isServicesHealthy === null) {
    return <PageLoader />
  }
  if (!isServicesHealthy) {
    return <ServicesDownPage />
  }

  return (
    <div
      className={`transition-opacity duration-300 ${
        shouldShowApp ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <AppProviders />
    </div>
  )
}

export default App
